import React from "react";
import "./InteractiveQuestion.css";

interface InteractiveQuestionProps {
  content: string;
  onOptionClick: (selectedOption: string) => void;
}

/**
 * Erkennt und rendert Interactive-Agent-Rückfragen mit klickbaren Buttons
 *
 * Pattern-Erkennung für Rückfragen:
 * - Beginnt mit Phrasen wie "Ihre Frage ist sehr allgemein" oder "Um Ihnen präzise zu antworten"
 * - Enthält "Meinen Sie..." gefolgt von einer Liste
 * - Hat Bullet-Point-Optionen (mit - oder * markiert)
 * - Endet oft mit "Bitte präzisieren Sie"
 */
export const InteractiveQuestion: React.FC<InteractiveQuestionProps> = ({
  content,
  onOptionClick
}) => {
  const parsed = parseInteractiveQuestion(content);

  if (!parsed) {
    // Kein Interactive-Question-Pattern erkannt
    return null;
  }

  const { preamble, options, conclusion } = parsed;

  return (
    <div className="interactive-question">
      {/* Einleitungstext */}
      {preamble && (
        <div className="interactive-preamble">{preamble}</div>
      )}

      {/* Optionen als Buttons */}
      <div className="interactive-options">
        {options.map((option, index) => (
          <button
            key={index}
            className="interactive-option-btn"
            onClick={() => onOptionClick(option)}
            title={`Klicken Sie um zu antworten: ${option}`}
          >
            <span className="option-number">{index + 1}</span>
            <span className="option-text">{option}</span>
          </button>
        ))}
      </div>

      {/* Hinweis für eigene Antworten */}
      <div className="interactive-custom-hint">
        💡 Sie können auch eine eigene Frage unten im Chat-Eingabefeld eingeben
      </div>

      {/* Abschlusstext (z.B. "Bitte präzisieren Sie...") */}
      {conclusion && (
        <div className="interactive-conclusion">{conclusion}</div>
      )}
    </div>
  );
};

/**
 * Prüft ob der Content eine Interactive-Question ist und parsed die Struktur
 */
export function isInteractiveQuestion(content: string): boolean {
  return parseInteractiveQuestion(content) !== null;
}

/**
 * Parsed Interactive-Question-Content in seine Bestandteile
 */
function parseInteractiveQuestion(content: string): {
  preamble: string;
  options: string[];
  conclusion: string;
} | null {
  // Pattern für Interactive-Questions:
  // 1. Beginnt mit einer Phrase die eine Rückfrage ankündigt
  // 2. Enthält "Meinen Sie..." oder ähnliche Formulierungen
  // 3. Hat eine Bullet-Point-Liste mit Optionen
  // 4. Optional: Abschluss mit "Bitte präzisieren Sie..."

  // Bereinige Content: Entferne Markdown-Code-Block-Syntax (```)
  let cleanedContent = content.trim();

  // Entferne ``` am Anfang und Ende
  if (cleanedContent.startsWith('```')) {
    cleanedContent = cleanedContent.replace(/^```[a-z]*\n?/i, '');
  }
  if (cleanedContent.endsWith('```')) {
    cleanedContent = cleanedContent.replace(/\n?```$/i, '');
  }

  const interactivePatterns = [
    /Ihre Frage ist sehr allgemein/i,
    /Um Ihnen präzise zu antworten/i,
    /möchte ich wissen/i,
    /Meinen Sie\.\.\./i,
    /Welchen Aspekt interessiert Sie/i,
  ];

  // Prüfe ob es ein Interactive-Question-Pattern enthält
  const hasInteractivePattern = interactivePatterns.some(pattern =>
    pattern.test(cleanedContent)
  );

  if (!hasInteractivePattern) {
    return null;
  }

  // Prüfe ob es eine Bullet-Point-Liste hat
  const lines = cleanedContent.split('\n');
  const bulletPoints = lines.filter(line =>
    /^\s*[-*]\s+/.test(line) // Zeilen die mit - oder * beginnen
  );

  if (bulletPoints.length < 2) {
    // Mindestens 2 Optionen nötig
    return null;
  }

  // Parse die Struktur
  const preambleLines: string[] = [];
  const options: string[] = [];
  const conclusionLines: string[] = [];

  let currentSection: 'preamble' | 'options' | 'conclusion' = 'preamble';

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      continue; // Leere Zeilen überspringen
    }

    // Bullet-Point-Option?
    const bulletMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (bulletMatch) {
      currentSection = 'options';
      options.push(bulletMatch[1].trim());
      continue;
    }

    // Text nach den Optionen?
    if (currentSection === 'options' && options.length > 0) {
      currentSection = 'conclusion';
      conclusionLines.push(trimmed);
      continue;
    }

    // Text vor den Optionen
    if (currentSection === 'preamble') {
      preambleLines.push(trimmed);
    }
  }

  // Validierung: Mindestens 2 Optionen
  if (options.length < 2) {
    return null;
  }

  return {
    preamble: preambleLines.join(' '),
    options,
    conclusion: conclusionLines.join(' ')
  };
}
